/**
 * Who is at the kiosk, in the one form the live status socket may say it [03-us-kad-6j-f § *Which fields give
 * a name*; decisions 0216, 0382 § Ruling 1].
 *
 * `{firstName, lastInitial}` and nothing else: ⛔ never a surname, a factor, an email or a candidate id. The
 * admin pane renders it as *Sofia R.*.
 *
 * ⭐ **A result the gateway would refuse is no candidate at all.** The bounds are `iss-kiosk-api`'s own
 * `readSessionStarted` bounds [KAD-6j-e AC-3], mirrored in `liveSocket.contract.ts` with every other value
 * this app copies from the api, because a frame the gateway refuses closes the socket `1008` — and ⛔ the
 * kiosk never sends a frame the gateway closes.
 */
import {
  MAX_FIRST_NAME_LENGTH,
  ONE_LETTER,
  REFUSED_IN_A_NAME,
} from '@/services/liveSocket.contract';

/** The candidate a session is held under — 0216's form, exactly. */
export interface SessionCandidate {
  readonly firstName: string;
  readonly lastInitial: string;
}

/**
 * ⭐ **Stricter than the gateway, on purpose** [derived: 03-us-kad-6j-f AC-6 and the actor table — *no
 * surname, no email in any frame*; 0216's form]. *Your name* is free text, so its first word can be an
 * email (`sofia.r@gmail.com`) or a surname written first (`Rodriguez, Sofia`), and either would travel as
 * `firstName`. A first name carrying an `@` or a comma gives no candidate. Being stricter keeps the kiosk
 * inside what the gateway accepts.
 *
 * ↻ **A digit no longer refuses a name: it is removed** ({@link withoutDigits}) [03-us-kad-6j-h AC-6; 0454].
 * The `@` and the comma still refuse, because removing them would show more than 0216 allows: a whole surname
 * (`García, Sofía` → `García S.`) or the local part of an email.
 *
 * ⚠️ **Tested on the text and its NFKC form, and the ORIGINAL is sent** [PR 250 review 2 item A; 0454 § The
 * rule, step 1]: NFKC folds the look-alikes (full-width `＠` and `，`, small `﹫`, `🄁` → `0,`) onto the
 * characters above, so a keyboard's full-width mode cannot walk an email or a comma past the fence. The
 * ideographic `、` (U+3001) and Arabic `،` (U+060C) commas do not fold, so they are named.
 */
const NOT_A_FIRST_NAME = /[@,\u3001\u060C]/u;

/** A decimal digit of any script [0454 § The rule, step 2] — tested on a character's NFKC form. */
const A_DIGIT = /\p{Nd}/u;

/** At least one letter, of any script [0454 § The rule, step 3]. */
const A_LETTER = /\p{L}/u;

/** One or more whitespace characters — what separates the words of *Your name*. */
const WHITESPACE = /\s+/u;

/**
 * Whether a first name fails the fence: a character {@link REFUSED_IN_A_NAME} names, an `@` or a comma — on
 * the text as given and on its NFKC form [0454 § The rule, step 1].
 */
function refusedAsAFirstName(firstName: string): boolean {
  return [firstName, firstName.normalize('NFKC')].some(
    (form) => REFUSED_IN_A_NAME.test(form) || NOT_A_FIRST_NAME.test(form),
  );
}

/**
 * The text as typed, minus every character whose NFKC form holds a decimal digit [03-us-kad-6j-h AC-6; 0454 §
 * The rule, step 2]: `3`, `٣`, `２`, `①`, `²`, and `½` (NFKC `1⁄2`), removed whole, wherever it sits
 * (`S0fía` → `Sfía`). Nothing takes its place.
 *
 * ⛔ **The kept characters are the ORIGINAL ones, never their NFKC form**: the fold only decides what goes.
 * Read by code point, so an astral character is removed whole, never by half a surrogate pair.
 */
function withoutDigits(text: string): string {
  return Array.from(text)
    .filter((character) => !A_DIGIT.test(character.normalize('NFKC')))
    .join('');
}

/**
 * The candidate a first name and a surname give — or `null` when they give none.
 *
 * ↻ **In this order** [03-us-kad-6j-h AC-6; 0454 § The rule]:
 * 1. **Refused first.** The first name as given fails {@link refusedAsAFirstName}: no candidate. This runs
 *    BEFORE the removal, so a character that folds to a digit AND a comma (`🄁`, NFKC `0,`) cannot take the
 *    comma out with it.
 * 2. **Digits removed** ({@link withoutDigits}) from the first name AND the surname whose initial is taken
 *    (`2Moreno` → `M`).
 * 3. **Cut.** `firstName` is what is left, trimmed: empty, longer than {@link MAX_FIRST_NAME_LENGTH}, or with
 *    no letter (`--`, from `555-123-4567` or typed) gives no candidate. All counted on what is left, never on
 *    what was typed [KAD-6j-e AC-3].
 *
 * `lastInitial` is the surname's first character once its digits are gone, trimmed and upper-cased
 * (`Sofia R.`). ⛔ The first character, never the first letter found further in: a surname that opens with
 * an apostrophe gives no initial rather than a guessed one, and so no candidate [03-us-kad-6j-f § *Which
 * fields give a name* — *"an initial that is not a letter"* gives no candidate; eval-kad-6j-f F16].
 */
export function sessionCandidateFromNames(
  firstName: string,
  surname: string,
): SessionCandidate | null {
  const first = firstNameLeft(firstName);
  if (first === null) return null;
  const opening = Array.from(withoutDigits(surname).trim())[0];
  if (opening === undefined) return null;
  // Upper-casing can lengthen a letter (`ß` → `SS`); the check runs on what would be sent.
  const lastInitial = opening.toUpperCase();
  if (!ONE_LETTER.test(lastInitial)) return null;
  return { firstName: first, lastInitial };
}

/**
 * The candidate *Your name* gives — the free-text field on *Need help?*.
 *
 * The first word is the first name. The initial is the last word's when there are two or more words, and
 * otherwise the check-in surname's, if one was typed this session. ⛔ A lone word with no surname typed
 * gives no candidate: nothing is invented to fill the initial.
 *
 * ↻ **The first name is tested TWICE** [03-us-kad-6j-h AC-6; 0454 § The rule, step 1]: the first word as
 * typed, here, before the removal; then the first word LEFT once the digits are removed from the whole text,
 * in {@link sessionCandidateFromNames}. The removal runs before the split, so a word made only of digits
 * disappears and the next word becomes the first name: `3 García, Sofía` → `García,`, refused. ⛔ The whole
 * text is never tested: `Sofía García, Jr` still gives `Sofía` · `J` [0454 § Not changed].
 */
export function sessionCandidateFromYourName(
  yourName: string,
  checkInSurname: string,
): SessionCandidate | null {
  if (refusedAsAFirstName(firstWordOf(yourName.trim().split(WHITESPACE)))) return null;
  const words = withoutDigits(yourName).trim().split(WHITESPACE);
  const surname = words.length >= 2 ? words.slice(-1).join('') : checkInSurname;
  return sessionCandidateFromNames(firstWordOf(words), surname);
}

/**
 * The name the queue entry carries for a *Your name* the candidate typed, as the recruiter's row shows it —
 * or `undefined` when the api would drop it [03-us-kop-1d AC-5; 0454; 0441 point 5].
 *
 * AC-5's cut for a typed name: its first word, plus the initial of its last word **when there are two or
 * more** (`Zzzqa Welcome` → `Zzzqa W.`; a lone `Dana` → `Dana`). The same fence, removal and bounds as
 * {@link sessionCandidateFromYourName}, but ⛔ no check-in surname: the api never sees one, so the initial is
 * the typed text's or there is none. A last word whose initial is not one letter drops the whole name, as it
 * does on the api.
 *
 * Display only — what the *Need help?* box shows when the session holds no name [03-us-kid-7 O4]. ⛔ Never
 * sent: the api cuts the name it receives itself.
 */
export function queueNameFromYourName(yourName: string): string | undefined {
  if (refusedAsAFirstName(firstWordOf(yourName.trim().split(WHITESPACE)))) return undefined;
  const words = withoutDigits(yourName).trim().split(WHITESPACE);
  if (words.length >= 2) {
    const candidate = sessionCandidateFromNames(firstWordOf(words), words.slice(-1).join(''));
    return candidate === null ? undefined : inQueueForm(candidate);
  }
  return firstNameLeft(firstWordOf(words)) ?? undefined;
}

/** 0216's form as the recruiter's row prints it: `Sofia R.`. */
export function inQueueForm(candidate: SessionCandidate): string {
  return `${candidate.firstName} ${candidate.lastInitial}.`;
}

/**
 * Steps 1–3 of {@link sessionCandidateFromNames} for the first name alone: refused, digits removed, cut — or
 * `null` when nothing that may be shown is left.
 */
function firstNameLeft(firstName: string): string | null {
  if (refusedAsAFirstName(firstName.trim())) return null;
  const first = withoutDigits(firstName).trim();
  if (first.length === 0 || first.length > MAX_FIRST_NAME_LENGTH) return null;
  if (!A_LETTER.test(first)) return null;
  return first;
}

/**
 * The first of `split`'s words. `slice` + `join` rather than an index: `split` always returns at least one
 * word, and an index would need a fallback for a case that cannot happen.
 */
function firstWordOf(words: readonly string[]): string {
  return words.slice(0, 1).join('');
}
